import React, { useState } from 'react';
import { 
  DataTable, 
  FilterPanel, 
  Pagination, 
  Badge, 
  createStatusBadge, 
  createTagBadge,
  Button,
  Checkbox
} from '@h2/ui';
import { Search } from 'lucide-react';
import Icon from '../ui/icon';
import OrderDetailModal from './OrderDetailModal';
import type { JamakeDataHook } from '../../hooks/useJamakeData';
import type { OrderStatus, OrderTag } from '../../stores/useOrderStore';

interface JamakeViewProps {
  data: JamakeDataHook;
  onStartWorkflow?: (orderIds: string[]) => void;
}

const JamakeView: React.FC<JamakeViewProps> = ({ data, onStartWorkflow }) => {
  const { currentPageOrders, pagination, filters, selection, actions, computed } = data;
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [singleOrderForDetail, setSingleOrderForDetail] = useState<any | null>(null);

  // 헤더 체크박스 상태 계산
  const headerCheckboxState = React.useMemo(() => {
    const allSelected = currentPageOrders.length > 0 && currentPageOrders.every(order => selection.selectedIds.includes(order.id));
    console.log('Header checkbox state recalculated:', {
      currentPageCount: currentPageOrders.length,
      selectedCount: selection.selectedIds.length,
      allSelected,
      selectedIds: selection.selectedIds,
      currentPageIds: currentPageOrders.map(o => o.id)
    });
    return allSelected;
  }, [currentPageOrders, selection.selectedIds]);

  // 상세보기 버튼으로 단일 주문 모달 열기
  const handleViewDetails = (orderId: string) => {
    console.log('handleViewDetails called for orderId:', orderId);
    
    // 전체 주문에서 해당 주문 찾기
    const targetOrder = currentPageOrders.find(order => order.id === orderId);
    console.log('Found target order:', targetOrder);
    console.log('Target order jobs:', targetOrder?.jobs);
    
    if (targetOrder) {
      setSingleOrderForDetail(targetOrder);
      setIsDetailModalOpen(true);
    }
  };

  // 등록 버튼 클릭 핸들러
  const handleAcceptOrders = () => {
    console.log('등록 처리 시작 - 선택된 주문들:', selection.selectedOrders);
    
    // 워크플로우 시작
    if (onStartWorkflow) {
      onStartWorkflow(selection.selectedIds);
    }
  };

  // 헤더 체크박스 핸들러 (배치 처리 방식)
  const handleHeaderCheckboxChange = () => {
    const currentPageOrderIds = currentPageOrders.map(order => order.id);
    const allCurrentSelected = currentPageOrderIds.every(id => selection.selectedIds.includes(id));
    
    console.log('Header checkbox clicked');
    console.log('Current page order IDs:', currentPageOrderIds);
    console.log('Currently selected IDs:', selection.selectedIds);
    console.log('All current selected?', allCurrentSelected);
    
    if (allCurrentSelected) {
      // 현재 페이지의 모든 항목이 선택된 경우 -> 현재 페이지 선택 해제
      console.log('Deselecting all current page items');
      const idsToDeselect = currentPageOrderIds.filter(id => selection.selectedIds.includes(id));
      idsToDeselect.forEach((id, index) => {
        setTimeout(() => actions.toggleSelection(id), index * 10);
      });
    } else {
      // 일부 또는 전체가 선택되지 않은 경우 -> 현재 페이지 전체 선택
      console.log('Selecting all current page items');
      const idsToSelect = currentPageOrderIds.filter(id => !selection.selectedIds.includes(id));
      idsToSelect.forEach((id, index) => {
        console.log('Selecting:', id, 'at index', index);
        setTimeout(() => actions.toggleSelection(id), index * 10);
      });
    }
  };

  // 등록 가능 여부 계산 (모든 선택된 주문이 '등록됨' 상태이고 같은 언어인지 확인)
  const canAcceptOrders = React.useMemo(() => {
    if (selection.selectedOrders.length === 0) return false;
    
    // 모든 주문이 '등록됨' 상태인지 확인
    const allRegistered = selection.selectedOrders.every(order => order.status === '등록됨');
    if (!allRegistered) return false;
    
    // 모든 주문의 원본언어가 동일한지 확인
    const allJobs = selection.selectedOrders.flatMap(order => order.jobs);
    if (allJobs.length === 0) return false;
    
    const firstJobSourceLang = allJobs[0]?.sourceLanguage;
    
    const allSameSourceLanguage = allJobs.every(job => 
      job.sourceLanguage === firstJobSourceLang
    );
    
    return allSameSourceLanguage;
  }, [selection.selectedOrders]);

  // ID 형식을 000으로 변환
  const formatOrderId = (id: string) => {
    return id.replace('ORD-', '');
  };

  // 체크박스 옵션들
  const statusOptions = [
    { value: '등록됨', label: '등록됨' },
    { value: '진행중', label: '진행중' },
    { value: '반려', label: '반려' },
    { value: '완료', label: '완료' }
  ];

  const tagOptions = [
    { 
      value: '블랙', 
      label: '블랙',
      variant: 'badge' as const,
      color: 'bg-black dark:bg-gray-800 text-white'
    },
    { 
      value: '원스텝', 
      label: '원스텝',
      variant: 'badge' as const,
      color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
    },
    { 
      value: '특수작업', 
      label: '특수작업',
      variant: 'badge' as const,
      color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* 필터 패널 */}
      <FilterPanel
        title="필터"
        activeFiltersCount={computed.activeFiltersCount}
        onReset={actions.resetFilters}
        resetLabel="초기화"
      >
        <FilterPanel.Grid columns={3}>
          <FilterPanel.Search
            value={filters.search}
            onChange={actions.setSearch}
            placeholder="고객명, 이메일, 제목 검색..."
            label="텍스트 검색"
          />
          <FilterPanel.DateRange
            startValue={filters.dateRange.start}
            endValue={filters.dateRange.end}
            onStartChange={(value) => actions.setDateRange({ start: value })}
            onEndChange={(value) => actions.setDateRange({ end: value })}
            startLabel="등록일자 (시작)"
            endLabel="등록일자 (종료)"
          />
        </FilterPanel.Grid>

        <FilterPanel.CheckboxGroup
          options={statusOptions}
          selectedValues={filters.statuses}
          onChange={(values) => {
            const validStatuses = values.filter((status): status is OrderStatus => 
              ['등록됨', '진행중', '반려', '완료'].includes(status)
            );
            actions.setStatuses(validStatuses);
          }}
          label="상태 필터"
          layout="horizontal"
        />

        <FilterPanel.CheckboxGroup
          options={tagOptions}
          selectedValues={filters.tags}
          onChange={(values) => {
            const validTags = values.filter((tag): tag is OrderTag => 
              ['블랙', '원스텝', '특수작업'].includes(tag)
            );
            actions.setTags(validTags);
          }}
          label="태그 필터"
          layout="horizontal"
        />
      </FilterPanel>

      {/* 로딩 상태 */}
      {computed.isLoading && (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-500 dark:text-gray-400">데이터를 불러오는 중...</div>
        </div>
      )}

      {/* 빈 상태 */}
      {computed.isEmpty && !computed.isLoading && (
        <div className="flex flex-col justify-center items-center py-12">
          <Icon name="Search" size={48} className="text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            검색 결과가 없습니다
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            다른 검색 조건을 시도해보세요.
          </p>
          <button
            onClick={actions.resetFilters}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            필터 초기화
          </button>
        </div>
      )}

      {/* 데이터 테이블 */}
      {!computed.isEmpty && !computed.isLoading && (
        <>
          {/* 선택 컨트롤 */}
          <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 px-4 py-3 rounded-lg border min-h-[56px]">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
              <span>
                총 {pagination.totalCount}개 주문
                {computed.selectedCount > 0 && (
                  <span className="font-medium text-blue-600 dark:text-blue-400 ml-2">
                    ({computed.selectedCount}개 선택됨)
                  </span>
                )}
              </span>
            </div>
            
            <div className="flex items-center gap-2 min-h-[32px]">
              {computed.selectedCount > 0 && (
                <>
                  {/* 등록 버튼 - 등록됨 상태만 선택되었을 때만 활성화 */}
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleAcceptOrders}
                    disabled={!canAcceptOrders}
                    className={`h-8 ${!canAcceptOrders ? 'opacity-30 cursor-not-allowed' : ''}`}
                  >
                    선택등록
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsDetailModalOpen(true)}
                    className="h-8"
                  >
                    선택상세
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={actions.clearSelection}
                    className="h-8"
                  >
                    선택 지우기
                  </Button>
                </>
              )}
            </div>
          </div>

          <DataTable variant="default" size="md">
            <DataTable.Header>
              <DataTable.Row>
                <DataTable.HeaderCell style={{ width: '50px' }}>
                  <Checkbox
                    checked={headerCheckboxState}
                    onCheckedChange={handleHeaderCheckboxChange}
                    aria-label="전체 선택"
                  />
                </DataTable.HeaderCell>
                <DataTable.HeaderCell style={{ width: '80px' }}>ID</DataTable.HeaderCell>
                <DataTable.HeaderCell style={{ width: '60px' }}>상세</DataTable.HeaderCell>
                <DataTable.HeaderCell>태그</DataTable.HeaderCell>
                <DataTable.HeaderCell>고객</DataTable.HeaderCell>
                <DataTable.HeaderCell>등록일자</DataTable.HeaderCell>
                <DataTable.HeaderCell>제목</DataTable.HeaderCell>
                <DataTable.HeaderCell>상태</DataTable.HeaderCell>
                <DataTable.HeaderCell>결제상태</DataTable.HeaderCell>
                <DataTable.HeaderCell>금액</DataTable.HeaderCell>
                <DataTable.HeaderCell>구독</DataTable.HeaderCell>
              </DataTable.Row>
            </DataTable.Header>

            <DataTable.Body>
              {currentPageOrders.map((order: any) => (
                <DataTable.Row 
                  key={order.id}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  onClick={() => actions.toggleSelection(order.id)}
                >
                  <DataTable.Cell 
                    style={{ width: '50px' }}
                    className="align-middle"
                    onClick={(e: any) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selection.selectedIds.includes(order.id)}
                      onCheckedChange={() => {
                        console.log('Individual checkbox clicked for:', order.id);
                        console.log('Current selection before toggle:', selection.selectedIds);
                        actions.toggleSelection(order.id);
                      }}
                      aria-label={`${order.id} 선택`}
                    />
                  </DataTable.Cell>
                  <DataTable.Cell style={{ width: '80px' }} className="font-medium whitespace-nowrap">
                    {formatOrderId(order.id)}
                  </DataTable.Cell>
                  <DataTable.Cell 
                    style={{ width: '60px' }} 
                    className="align-middle"
                    onClick={(e: any) => e.stopPropagation()}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewDetails(order.id)}
                      className="h-8 w-8 p-0"
                    >
                      <Search className="h-4 w-4" />
                    </Button>
                  </DataTable.Cell>
                  
                  <DataTable.Cell minWidth="150px">
                    <div className="flex flex-nowrap gap-1 overflow-x-auto">
                      {order.tags.map((tag: any) => (
                        <Badge
                          key={tag}
                          {...createTagBadge(tag)}
                          size="xs"
                          className="whitespace-nowrap flex-shrink-0"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </DataTable.Cell>

                  <DataTable.Cell minWidth="200px">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {order.customer.name}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {order.customer.email}
                      </div>
                    </div>
                  </DataTable.Cell>

                  <DataTable.Cell minWidth="100px" className="whitespace-nowrap">
                    {order.registeredAt}
                  </DataTable.Cell>

                  <DataTable.Cell className="max-w-xs truncate">
                    {order.title}
                  </DataTable.Cell>

                  <DataTable.Cell minWidth="100px">
                    <Badge
                      {...createStatusBadge(order.status)}
                      size="xs"
                      className="whitespace-nowrap"
                    >
                      {order.status}
                    </Badge>
                  </DataTable.Cell>

                  <DataTable.Cell minWidth="100px">
                    <Badge
                      {...createStatusBadge(order.paymentStatus)}
                      size="xs"
                      className="whitespace-nowrap"
                    >
                      {order.paymentStatus}
                    </Badge>
                  </DataTable.Cell>

                  <DataTable.Cell minWidth="140px">
                    <div className="whitespace-nowrap">
                      {order.totalAmount.toLocaleString()}
                      <span className="text-gray-500 dark:text-gray-400">
                        ({order.paidAmount.toLocaleString()})
                      </span>
                    </div>
                  </DataTable.Cell>

                  <DataTable.Cell minWidth="60px" className="text-center">
                    {order.isSubscription ? (
                      <Icon name="CheckCircle" size={16} className="text-green-500 dark:text-green-400 mx-auto" />
                    ) : (
                      <Icon name="X" size={16} className="text-gray-400 dark:text-gray-500 mx-auto" />
                    )}
                  </DataTable.Cell>
                </DataTable.Row>
              ))}
            </DataTable.Body>
          </DataTable>

          {/* 페이지네이션 */}
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            pageSize={pagination.pageSize}
            totalCount={pagination.totalCount}
            onPageChange={actions.setCurrentPage}
            onPageSizeChange={actions.setPageSize}
            pageSizeOptions={[5, 10, 20, 50]}
            showPageSizeSelector={true}
            showInfo={true}
            showFirstLast={false}
            maxVisiblePages={5}
            size="md"
            variant="default"
          />
        </>
      )}

      {/* 선택 상세 모달 */}
      <OrderDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSingleOrderForDetail(null);
        }}
        selectedOrders={singleOrderForDetail ? [singleOrderForDetail] : selection.selectedOrders}
        onRemoveOrder={(orderId) => {
          // 단일 주문 상세보기에서는 제거 시 모달 닫기
          if (singleOrderForDetail) {
            setIsDetailModalOpen(false);
            setSingleOrderForDetail(null);
          } else {
            // 다중 선택에서는 해당 주문만 선택 해제
            actions.toggleSelection(orderId);
          }
        }}
        onAcceptOrders={handleAcceptOrders}
      />
    </div>
  );
};

export default JamakeView;
